import "maplibre-gl/dist/maplibre-gl.css";

import { MapController, MapView } from "@deck.gl/core";
import DeckGL from "@deck.gl/react";
import type { GeoJSON } from "geojson";
import { useEffect, useMemo, useRef, useState } from "react";
import * as maplibre from "react-map-gl/maplibre";

import { mapStyle } from "~/lib/map/components/constants";
import { useViewMap } from "~/lib/map/hooks";
import { getFittedViewStateFromGeoJson } from "~/lib/map/utils";

const MAP_HEIGHT = 460;

interface AreaViewMapProps {
  areaH3Indexes: string[];
  geometry: GeoJSON | null;
  fitToGeometry?: boolean;
}

/** Read-only area map: renders a geo-item's H3 hexagons or GeoJSON. Reuses the shared `useViewMap`. */
export function AreaViewMap({ areaH3Indexes, geometry, fitToGeometry = false }: AreaViewMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-[var(--radius-md)] border border-border-default"
      style={{ height: MAP_HEIGHT }}
    >
      {width > 0 && (
        <AreaViewMapCanvas
          width={width}
          areaH3Indexes={areaH3Indexes}
          geometry={geometry}
          fitToGeometry={fitToGeometry}
        />
      )}
    </div>
  );
}

function AreaViewMapCanvas({ width, areaH3Indexes, geometry, fitToGeometry }: AreaViewMapProps & { width: number }) {
  const initialViewState = useMemo(
    () => (fitToGeometry && geometry ? getFittedViewStateFromGeoJson(geometry, width, MAP_HEIGHT) : undefined),
    [fitToGeometry, geometry, width],
  );
  const { layers, viewState } = useViewMap({
    width,
    height: MAP_HEIGHT,
    areaH3Indexes,
    geometry,
    initialViewState,
  });

  return (
    <DeckGL
      id="area-view-map"
      initialViewState={viewState}
      layers={layers}
      views={new MapView({ repeat: true })}
      controller={{ type: MapController, dragRotate: false }}
      width="100%"
      height={MAP_HEIGHT}
    >
      <maplibre.Map key="area-view-map" mapStyle={mapStyle} />
    </DeckGL>
  );
}
